import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Wrench, ShieldCheck, Calendar, Boxes, ClipboardList,
  AlertOctagon, FileText, Truck, FolderOpen, Inbox, Settings, LogOut, Search, BarChart3, Bell,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/maintenance", label: "Maintenance", icon: Wrench, testid: "nav-maintenance" },
  { to: "/compliance", label: "Compliance", icon: ShieldCheck, testid: "nav-compliance" },
  { to: "/calendar", label: "Calendar", icon: Calendar, testid: "nav-calendar" },
  { to: "/assets", label: "Assets", icon: Boxes, testid: "nav-assets" },
  { to: "/inspections", label: "Inspections", icon: ClipboardList, testid: "nav-inspections" },
  { to: "/defects", label: "Defects", icon: AlertOctagon, testid: "nav-defects" },
  { to: "/work-orders", label: "Work Orders", icon: FileText, testid: "nav-work-orders" },
  { to: "/suppliers", label: "Suppliers", icon: Truck, testid: "nav-suppliers" },
  { to: "/documents", label: "Documents", icon: FolderOpen, testid: "nav-documents" },
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
  { to: "/import-review", label: "Import Review", icon: Inbox, testid: "nav-import-review" },
  { to: "/administration", label: "Administration", icon: Settings, testid: "nav-administration" },
];

function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const ref = useRef(null);
  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch (err) {
      console.warn("Notifications fetch failed", err);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const unread = items.filter((n) => !n.is_read).length;

  const markRead = async (n) => {
    try { await api.post(`/notifications/${n.id}/read`); }
    catch (err) { console.warn("Mark read failed", err); }
    load();
    setOpen(false);
    if (n.related_type === "maintenance_task") nav("/maintenance");
    else if (n.related_type === "compliance") nav("/compliance");
    else if (n.related_type === "defect") nav("/defects");
    else if (n.related_type === "supplier") nav(`/suppliers/${n.related_id}`);
  };

  const tone = (t) => t === "critical" ? "border-l-red-500" : t === "warning" ? "border-l-amber-500" : "border-l-slate-300";

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative h-9 w-9 flex items-center justify-center rounded-md border border-slate-200 hover:bg-slate-50"
        data-testid="notifications-bell"
      >
        <Bell className="w-4 h-4 text-slate-700"/>
        {unread > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notifications-count">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-96 max-h-[32rem] overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg z-50" data-testid="notifications-dropdown">
          <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
            <div className="font-heading text-sm font-semibold">Notifications</div>
            <div className="text-xs text-slate-500">{unread} unread</div>
          </div>
          {items.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">No notifications.</div>
          ) : items.map((n) => (
            <button
              key={n.id}
              onClick={() => markRead(n)}
              className={`w-full text-left px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 border-l-4 ${tone(n.notif_type)} ${n.is_read?"opacity-60":""}`}
              data-testid={`notification-item-${n.id}`}
            >
              <div className="text-sm font-medium text-slate-900">{n.title}</div>
              <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{n.message}</div>
              <div className="text-[11px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setResults(null); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const doSearch = async (v) => {
    setQuery(v);
    if (v.length < 2) { setResults(null); return; }
    try {
      const { data } = await api.get("/search", { params: { q: v } });
      setResults(data);
    } catch (err) {
      console.warn("Global search failed", err);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50 text-slate-900">
      <aside className="w-64 shrink-0 bg-slate-900 text-slate-100 flex flex-col" data-testid="app-sidebar">
        <div className="px-5 py-5 border-b border-slate-800">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-400">Shady Acres</div>
          <div className="mt-1 font-heading text-lg font-semibold">Maintenance &amp; Compliance</div>
        </div>
        <nav className="flex-1 py-3 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors duration-200 border-l-2 ${
                  isActive
                    ? "bg-slate-800 text-white border-blue-500"
                    : "text-slate-300 hover:bg-slate-800 hover:text-white border-transparent"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-800">
          <div className="text-xs text-slate-400">Signed in as</div>
          <div className="text-sm font-medium truncate">{user?.email}</div>
          <div className="text-xs mt-0.5"><Badge variant="secondary" className="bg-slate-800 text-slate-200 border-0">{user?.role}</Badge></div>
          <Button
            variant="ghost"
            size="sm"
            className="mt-3 w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800"
            data-testid="logout-btn"
            onClick={async () => { await logout(); navigate("/login"); }}
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 flex flex-col">
        <header className="h-14 bg-white border-b border-slate-200 flex items-center px-6 gap-4">
          <div ref={boxRef} className="relative w-full max-w-lg">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={query}
              onChange={(e) => doSearch(e.target.value)}
              placeholder="Search assets, work orders, suppliers…"
              className="pl-9 bg-slate-50 border-slate-200 focus-visible:ring-2 focus-visible:ring-blue-500"
              data-testid="global-search-input"
            />
            {results && (
              <div className="absolute z-50 left-0 right-0 mt-2 bg-white border border-slate-200 rounded-md shadow-lg max-h-96 overflow-y-auto" data-testid="global-search-results">
                {["assets","work_orders","suppliers","defects","documents"].map((k) =>
                  results[k]?.length ? (
                    <div key={k}>
                      <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 px-3 pt-2">{k.replace("_"," ")}</div>
                      {results[k].map((r) => (
                        <button
                          key={r.id}
                          className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm border-b border-slate-100 last:border-0"
                          onClick={() => {
                            setResults(null); setQuery("");
                            if (k === "assets") navigate(`/assets/${r.id}`);
                            else if (k === "suppliers") navigate(`/suppliers/${r.id}`);
                            else if (k === "work_orders") navigate(`/work-orders`);
                            else if (k === "defects") navigate(`/defects`);
                            else if (k === "documents") navigate(`/documents`);
                          }}
                        >
                          <div className="font-medium text-slate-900">{r.name || r.wo_number || r.description || r.original_filename}</div>
                          <div className="text-xs text-slate-500">{r.asset_group || r.service_area || r.status || r.category || ""}</div>
                        </button>
                      ))}
                    </div>
                  ) : null,
                )}
                {Object.values(results).every((v) => !v?.length) && (
                  <div className="p-4 text-sm text-slate-500">No results</div>
                )}
              </div>
            )}
          </div>
          <div className="ml-auto flex items-center gap-3">
            <Button size="sm" onClick={() => navigate("/inspections")} data-testid="quick-inspection-btn">+ Inspection</Button>
            <Button size="sm" variant="outline" onClick={() => navigate("/defects")} data-testid="quick-defect-btn">+ Defect</Button>
            <Button size="sm" variant="outline" onClick={() => navigate("/work-orders")} data-testid="quick-workorder-btn">+ Work Order</Button>
            <NotificationsBell />
          </div>
        </header>
        <div className="p-6 flex-1 overflow-y-auto" data-testid="page-container">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
